/**
 * OPEN-136 / DEC-377 — "this tank has no BBL per inch", with the standard catalog's answer.
 *
 * A tank's gauge estimate needs its bbl per inch. Most haulers never enter one (Corbin), so the
 * estimate already uses the standard tank catalog: the catalog row the tank is linked to, else
 * the one value for its capacity (shared suggestBblPerInch). This hint says so on the tank, and
 * lets a Dispatch Manager save the value with one click (PATCH /v1/tanks/:id, the edit form's own
 * endpoint):
 *   - one value: "estimates use 1.67 from the standard 400 BBL 12FT" + Save 1.67;
 *   - several (500 BBL is 2.75 or 2.79: 15 ft 6 in or 15 ft 6.5 in across): no estimate; one
 *     button per value to pick it;
 *   - none (no capacity, or a size the catalog lacks): no estimate; enter it with Edit.
 * Only bblPerInch is saved: the tank's type is not this rule's business (Corbin: "all of these
 * tanks hold water"). Nothing shows once the tank has its own value. Without
 * MANAGE_HAULER_CATALOG the text shows and the buttons don't (the API is the real gate).
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import type { BblPerInchChoice, BblPerInchSuggestion } from '@lodex-pro/shared/tank-strapping';

export interface TankSizeHintProps {
  readonly tankId: string;
  /** The tank's own bbl per inch; the hint shows only when this is null. */
  readonly bblPerInch: string | null;
  /** The catalog's answer for this tank (suggestBblPerInch, or the server's choices). */
  readonly suggestion: BblPerInchSuggestion;
  readonly canManage: boolean;
  /** Where to send someone without a Save button, e.g. "with Edit" or "in Sites & Operators". */
  readonly whereToSet: string;
  /** The queries to refresh after a save: whatever lists the tank. */
  readonly refresh: readonly (readonly string[])[];
}

const hintStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-warning)' };

const saveStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-accent-blue)',
  background: 'none',
  border: 'none',
  padding: '0 0 0 6px',
  cursor: 'pointer',
};

/** The server's choices for a tank (GET /v1/tanks/levels) as a suggestion: one = that value. */
export function suggestionFrom(
  choices: readonly BblPerInchChoice[] | undefined,
): BblPerInchSuggestion {
  const list = [...(choices ?? [])];
  return { bblPerInch: list.length === 1 ? list[0]!.bblPerInch : null, choices: list };
}

/** "5.2400" → "5.24". */
export function shortBpi(v: string | number): string {
  return String(Number(v));
}

export function TankSizeHint({
  tankId,
  bblPerInch,
  suggestion,
  canManage,
  whereToSet,
  refresh,
}: TankSizeHintProps): React.JSX.Element | null {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const save = useApiMutation<{ bblPerInch: number }, { id: string }>(
    opts,
    `/v1/tanks/${tankId}`,
    'PATCH',
  );
  if (bblPerInch !== null) return null;

  const { bblPerInch: single, choices } = suggestion;
  const pick = (value: string): void => {
    save.mutate(
      { bblPerInch: Number(value) },
      {
        onSuccess: () => {
          toast.success(`Saved ${shortBpi(value)} BBL per inch`);
          for (const queryKey of refresh) void qc.invalidateQueries({ queryKey });
        },
        onError: () => toast.error('Could not save the BBL per inch. Try again.'),
      },
    );
  };
  // One choice per distinct value, so the value is a unique key (review LOW-2).
  const button = (value: string, label: string): React.JSX.Element => (
    <button
      key={value}
      type="button"
      style={saveStyle}
      disabled={save.isPending}
      onClick={(): void => pick(value)}
      data-testid="tank-size-save"
    >
      {label}
    </button>
  );

  if (single !== null) {
    return (
      <div style={hintStyle} data-testid="tank-size-hint" data-kind="SUGGEST">
        No BBL per inch · estimates use {shortBpi(single)} from the standard {choices[0]!.name}
        {canManage && button(single, `Save ${shortBpi(single)}`)}
      </div>
    );
  }
  if (choices.length > 1) {
    return (
      <div style={hintStyle} data-testid="tank-size-hint" data-kind="AMBIGUOUS">
        No BBL per inch, so no estimate · {shortBpi(choices[0]!.capacityBbl)} BBL could be{' '}
        {choices.map((c) => `${shortBpi(c.bblPerInch)} (${c.name})`).join(' or ')}
        {canManage
          ? choices.map((c) => button(c.bblPerInch, `Use ${shortBpi(c.bblPerInch)}`))
          : ` · set it ${whereToSet}`}
      </div>
    );
  }
  return (
    <div style={hintStyle} data-testid="tank-size-hint" data-kind="MISSING">
      No BBL per inch, so no estimate · set it {whereToSet}
    </div>
  );
}
